// A descriptor list: Tagify over the WSET whitelist (free-form allowed) and
// the SAT pill tree beneath it, kept in step both ways. The controller's
// own list is the source of truth; Tagify removes tags asynchronously and
// debounces its writes to the original input, so the input's value is
// written here on every change and the form always posts the current list.
// Settings ported from the legacy DescriptorTagInput (Tasting.tsx).
import { Controller } from "@hotwired/stimulus"
import Tagify from "@yaireo/tagify"

const MAX_DESCRIPTORS = 80 // mirrors the API's cap on descriptor arrays

export default class extends Controller {
  static targets = ["input", "pill", "group", "count", "chosen"]

  connect() {
    this.whitelist = [...new Set(this.pillTargets.map((pill) => pill.dataset.descriptor))]
    this.canonical = new Map(this.whitelist.map((d) => [d.toLowerCase(), d]))
    this.values = this.normalize(this.inputTarget.value.split(","))
    this.programmatic = false

    const canonical = this.canonical
    this.tagify = new Tagify(this.inputTarget, {
      whitelist: this.whitelist,
      enforceWhitelist: false, // free-form descriptors are allowed and saved
      duplicates: false,
      trim: true,
      delimiters: ",",
      maxTags: MAX_DESCRIPTORS,
      skipInvalid: false,
      dropdown: {
        enabled: 0, // show suggestions on focus, before any typing
        maxItems: 30,
        fuzzySearch: true,
        caseSensitive: false,
        closeOnSelect: false,
        highlightFirst: true
      },
      originalInputValueFormat: (tags) => tags.map((tag) => tag.value).join(","),
      transformTag(tagData) {
        const match = canonical.get(tagData.value.trim().toLowerCase())
        if (match) {
          tagData.value = match
          tagData.class = "tagify__tag--wset"
        } else {
          tagData.class = "tagify__tag--free"
        }
      }
    })

    this.tagify.on("change", () => {
      if (this.programmatic) return
      const next = this.normalize(this.tagify.value.map((tag) => tag.value))
      if (this.same(next, this.values)) return
      this.values = next
      this.sync()
    })

    this.onSubmit = () => this.writeInput()
    this.form = this.element.closest("form")
    this.form?.addEventListener("submit", this.onSubmit)
    this.writeInput()
    this.paintPills()
    this.openChosenGroups()
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.onSubmit)
    this.tagify?.destroy()
    this.tagify = null
  }

  // Replace the whole list (the aroma → flavour carry-over).
  setValues(list) {
    this.values = this.normalize(list)
    this.programmatic = true
    this.tagify.removeAllTags()
    if (this.values.length) this.tagify.addTags(this.values)
    this.programmatic = false
    this.sync()
    this.openChosenGroups()
  }

  toggle(event) {
    const descriptor = event.currentTarget.dataset.descriptor
    const key = descriptor.toLowerCase()
    const present = this.values.some((value) => value.toLowerCase() === key)
    this.programmatic = true
    if (present) {
      const matching = this.values.filter((value) => value.toLowerCase() === key)
      this.values = this.values.filter((value) => value.toLowerCase() !== key)
      this.tagify.removeTags(matching, false, 0)
    } else {
      this.values = this.normalize([...this.values, descriptor])
      this.tagify.addTags([descriptor])
    }
    this.programmatic = false
    this.sync()
  }

  // Trim, canonicalise WSET spelling, dedupe case-insensitively, cap at 80.
  normalize(list) {
    const seen = new Set()
    const out = []
    for (const raw of list) {
      const trimmed = String(raw ?? "").trim()
      if (!trimmed) continue
      const value = this.canonical.get(trimmed.toLowerCase()) ?? trimmed
      const key = value.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      out.push(value)
      if (out.length >= MAX_DESCRIPTORS) break
    }
    return out
  }

  same(a, b) {
    if (a.length !== b.length) return false
    const set = new Set(b.map((value) => value.toLowerCase()))
    return a.every((value) => set.has(value.toLowerCase()))
  }

  sync() {
    this.writeInput()
    this.paintPills()
    this.element.dispatchEvent(new CustomEvent("descriptor-tags:change", { bubbles: true, detail: { values: this.values } }))
  }

  writeInput() {
    this.inputTarget.value = this.values.join(",")
  }

  paintPills() {
    const selected = new Set(this.values.map((value) => value.toLowerCase()))
    this.pillTargets.forEach((pill) => {
      const on = selected.has(pill.dataset.descriptor.toLowerCase())
      pill.classList.toggle("active", on)
      pill.setAttribute("aria-pressed", String(on))
    })
    // The count stays empty at zero (the kit hides an empty count); the
    // screen reader's " chosen" after it shows and hides with it.
    this.groupTargets.forEach((group) => {
      const count = group.querySelectorAll('[aria-pressed="true"]').length
      const badge = group.querySelector('[data-descriptor-tags-target="count"]')
      const word = group.querySelector('[data-descriptor-tags-target="chosen"]')
      if (badge) badge.textContent = count > 0 ? String(count) : ""
      if (word) word.hidden = count === 0
    })
  }

  // On arrival, and when the aroma list is carried into flavour, a category
  // holding a choice opens. Not on every tap: a taster who folds a category
  // away should not see it spring back.
  openChosenGroups() {
    this.groupTargets.forEach((group) => {
      if (group.querySelector('[aria-pressed="true"]')) group.open = true
    })
  }
}
